import { useState, useMemo, useCallback, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { DataTable } from '@/components/data/DataTable'
import { Switch } from '@/components/ui/switch'
import { formatDateForLocale } from '@/lib/dateUtils'
import { effectiveIsGo } from '../components/WaitGoState'
import { useWorkforce } from '../assignment/api'
import { workforceTotals } from '../assignment/logic'
import { usePresence } from '@/contexts/PresenceContext'
import { Input } from '@/components/ui/input'
import { SegmentedControl } from '@/components/ui/segmented-control'
import { DependencyOrderView } from '../assignment/DependencyOrderView'
import { BySiteView } from '../assignment/BySiteView'
import { WorkforceView } from '../assignment/WorkforceView'
import { RankedAssignDialog } from '../assignment/RankedAssignDialog'
import { useRecoverRequests, useRequestIDOptions, useLocationOptions, useTeamOptions, useRecoverySetOptions, useScenarioOptions, useRecoverRequestStats, useAssignEngineer, useMarkComplete } from '../hooks'
import { createRecoverRequestColumns } from '../columns'
import { JsonViewDialog } from '@/components/ui/JsonViewDialog'
import { RunbookViewDialog } from '../components/RunbookViewDialog'
import { RecoverRequestDetailsDialog } from '../components/RecoverRequestDetailsDialog'
import { ScenarioInfoDialog } from '../components/ScenarioInfoDialog'
import { SubObjectProgressDialog } from '../components/SubObjectProgressDialog'
import { MarkCompleteConfirmDialog } from '@/features/my-assignments/components/MarkCompleteConfirmDialog'
import { ObjectQRCodeDialog } from '@/features/objects/components/ObjectQRCodeDialog'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Button } from '@/components/ui/button'
import { Search, X } from 'lucide-react'
import type { RecoverRequest } from '../api'
import { AssignmentCanvasDialog } from '../assignment/AssignmentCanvasDialog'
import { PlannedTimelineStrip } from '../assignment/PlannedTimelineStrip'
import { debounce } from '@/lib/utils'

/**
 * A compact pastel stat, matching the Resource view's cards.
 *
 * The three it replaces were 96px tall with a 4rem gutter beneath them and repeated the same
 * three figures on all four views — a third of the fold spent saying nothing that changed.
 */
function QueueStat({ label, value, note, tint }: {
    label: string
    value: string
    note?: string
    tint: string
}) {
    return (
        <div className={`rounded-xl px-4 py-3 ${tint}`}>
            <div className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">{label}</div>
            <div className="mt-1 text-2xl font-semibold tabular-nums">{value}</div>
            {note && <div className="text-[11px] text-muted-foreground">{note}</div>}
        </div>
    )
}

export function RecoverRequestListPage() {
    const { t } = useTranslation()
    const { i18n } = useTranslation()
    const [page, setPage] = useState(1)
    const [limit, setLimit] = useState(20)
    const [search, setSearch] = useState('')
    const [debouncedSearch, setDebouncedSearch] = useState('')
    const [requestIDFilter, setRequestIDFilter] = useState<string>('')
    const [locationFilter, setLocationFilter] = useState<string>('')
    const [teamFilter, setTeamFilter] = useState<string>('')
    const [recoverySetFilter, setRecoverySetFilter] = useState<string>('')
    const [scenarioFilter, setScenarioFilter] = useState<string>('')
    /**
     * Four readings of one queue.
     *
     * The existing table is the default and is untouched — people know it, and a new default would
     * make every returning user relearn the screen. The other three answer questions the flat
     * table cannot: what order the estate actually recovers in, where the work physically is, and
     * who there is to do it.
     */
    const [assignView, setAssignView] = useState<'existing' | 'dependency' | 'site' | 'workforce'>('existing')
    const [canvasOpen, setCanvasOpen] = useState(false)
    /** The row the ranked dialog is deciding about. */
    const [rankedRow, setRankedRow] = useState<RecoverRequest | null>(null)
    /**
     * Command and control's bypass policy.
     *
     * ON — an engineer may work around a bypassable dependency (break-glass local accounts, a flat
     * recovery VLAN), so an object whose only outstanding dependencies are bypassable is
     * assignable, and its button carries the count of bypasses they will have to apply.
     * OFF — those objects are WAIT like any other blocked work.
     *
     * DEFAULTS OFF. Allowing a bypass is a decision somebody has to take, and a screen that
     * assumes it has been taken shows work as assignable that nobody has agreed to work around.
     * The safe reading is the strict one; permitting bypasses is the deliberate act. Persisted,
     * because it describes how the recovery is being run rather than a per-visit preference.
     */
    const [allowBypass, setAllowBypass] = useState<boolean>(() => {
        try { return localStorage.getItem('vs-allow-bypass') === 'true' } catch { return false }
    })
    const toggleAllowBypass = useCallback((next: boolean) => {
        setAllowBypass(next)
        try { localStorage.setItem('vs-allow-bypass', String(next)) } catch { /* private mode; the session still works */ }
    }, [])
    const [jsonDialog, setJsonDialog] = useState<{ title: string; content: string | null } | null>(null)
    const [runbookDialog, setRunbookDialog] = useState<{ objectID: string; scenario: number | null; forecastStartTime: string | Date | null } | null>(null)
    const [detailsDialog, setDetailsDialog] = useState<{ objectID: string; requestID: string } | null>(null)
    const [showQRCodeDialog, setShowQRCodeDialog] = useState(false)
    const [qrCodeObjectID, setQrCodeObjectID] = useState<string | null>(null)
    const [subObjectProgressDialog, setSubObjectProgressDialog] = useState<{ mainId: string; objectDescription?: string | null } | null>(null)
    const [scenarioInfoModal, setScenarioInfoModal] = useState<{ requestID: string | null; scenario: number | null; objectID: string | null; objectDescription: string | null } | null>(null)
    const [markCompleteDialog, setMarkCompleteDialog] = useState<{ objectID: string; objectDescription: string | null; recoverRequestId: number | null } | null>(null)
    const markComplete = useMarkComplete()

    const { data: requestIDOptions } = useRequestIDOptions()
    const { data: locationOptions } = useLocationOptions()
    const { data: teamOptions } = useTeamOptions()
    const { data: recoverySetOptions } = useRecoverySetOptions()
    const { data: scenarioOptions } = useScenarioOptions()
    const { data: stats, isLoading: statsLoading } = useRecoverRequestStats()

    // Debounce Object ID search so we don't refetch on every keystroke
    const debouncedSetSearch = useMemo(() => debounce((value: string) => setDebouncedSearch(value), 350), [])
    // A search still waiting when the page closes is dropped, not applied to a page that is gone.
    useEffect(() => () => debouncedSetSearch.cancel(), [debouncedSetSearch])

    useEffect(() => {
        debouncedSetSearch(search)
    }, [search, debouncedSetSearch])

    const { data: recoverRequestsResponse } = useRecoverRequests({ 
        page, 
        perPage: limit,
        search: debouncedSearch || undefined,
        requestID: requestIDFilter || undefined,
        location: locationFilter || undefined,
        team: teamFilter || undefined,
        recoverySet: recoverySetFilter || undefined,
        scenario: scenarioFilter || undefined,
    })

    // THE WHOLE REQUEST, not the twenty rows on screen. The timeline describes the plan, and
    // handed the paginated page it drew only the hours those rows happened to fall in — so it
    // grew as you paged and shrank as you filtered, which made it a picture of the table rather
    // than of the recovery. Same query key as the canvas dialog uses, so this is one fetch.
    const requestAll = useRecoverRequests(
        requestIDFilter ? { requestID: requestIDFilter, perPage: 500, page: 1 } : undefined,
    )
    const requestAllRows: RecoverRequest[] = useMemo(() => {
        if (!requestIDFilter) return []
        const r: any = requestAll.data
        if (!r) return []
        const arr = r.data?.data || r.data
        return Array.isArray(arr) ? arr : []
    }, [requestAll.data, requestIDFilter])

    const recoverRequests: RecoverRequest[] = useMemo(() => {
        if (!recoverRequestsResponse) return []
        const dataArray = recoverRequestsResponse.data?.data || recoverRequestsResponse.data
        if (!dataArray || !Array.isArray(dataArray)) {
            return []
        }
        return dataArray
    }, [recoverRequestsResponse])

    /** Startable vs blocked, under the bypass policy in force — the same reading as the buttons. */
    // Counted over the WHOLE request when one is selected, not the twenty rows on screen.
    // Page-scoped, "startable now" answered "on this page", so it changed when you paged and
    // disagreed with both the timeline and the Resource view on the same screen. With no
    // request selected there is no bounded set to count, so the page remains the honest scope.
    const countedRows = requestIDFilter && requestAllRows.length > 0 ? requestAllRows : recoverRequests
    const goCount = useMemo(
        () => countedRows.filter((r) => effectiveIsGo(r, allowBypass)).length,
        [countedRows, allowBypass])
    const waitCount = countedRows.length - goCount

    /**
    /**
     * ALL FOUR CARDS CHANGE, not just the last one.
     *
     * A headline that says the same thing whatever you are looking at is furniture. The queue
     * views lead with the work — how much of it, how much can start, how much cannot; the Resource
     * view leads with the people, because that is the question it exists to answer. The Resource
     * screen used to draw those four itself in a SECOND row, so it carried eight cards where every
     * other view carried four.
     */
    const workforceQuery = useWorkforce(assignView === 'workforce', requestIDFilter || null)
    const { getUserStatus } = usePresence()
    const cards = useMemo(() => {
        if (assignView === 'workforce') {
            const engineers = workforceQuery.data?.engineers ?? []
            const w = workforceTotals(engineers, getUserStatus)
            return [
                { label: t('assignment.loggedIn', 'Logged in'), value: String(w.loggedIn),
                  note: t('assignment.ofRoster', 'of {{n}} on the roster', { n: engineers.length }),
                  tint: 'bg-sky-50 dark:bg-sky-950/40' },
                { label: t('assignment.workingRecovery', 'Working a recovery'), value: String(w.working),
                  note: t('assignment.workingNote', 'mid-runbook right now'),
                  tint: 'bg-amber-50 dark:bg-amber-950/40' },
                { label: t('assignment.available', 'Available'), value: String(w.available),
                  note: t('assignment.availableNote', 'logged in, nothing under way'),
                  tint: 'bg-emerald-50 dark:bg-emerald-950/40' },
                { label: t('assignment.away', 'Away'), value: String(w.away),
                  note: t('assignment.awayNote', 'logged in, marked away'),
                  tint: 'bg-violet-50 dark:bg-violet-950/40' },
            ]
        }

        const work = [
            { label: t('recoverRequests.numberObjects'),
              value: statsLoading ? '\u2026' : String(stats?.objectCount ?? 0),
              note: t('assignment.acrossRequests', 'across {{n}} requests', { n: stats?.uniqueRequestCount ?? 0 }),
              tint: 'bg-sky-50 dark:bg-sky-950/40' },
            { label: t('assignment.startableNow', 'Startable now'), value: String(goCount),
              note: t('assignment.dependenciesClear', 'dependencies clear'),
              tint: 'bg-emerald-50 dark:bg-emerald-950/40' },
            { label: t('assignment.blocked', 'Blocked'), value: String(waitCount),
              note: t('assignment.waitingOnDependencies', 'waiting on dependencies'),
              tint: 'bg-amber-50 dark:bg-amber-950/40' },
        ]

        if (assignView === 'site') {
            const sites = new Set(recoverRequests.map((r) => r.location).filter(Boolean))
            return [...work, { label: t('recoverRequests.numberLocations'), value: String(sites.size),
                note: t('assignment.sitesWithWork', 'with work on this page'),
                tint: 'bg-violet-50 dark:bg-violet-950/40' }]
        }
        if (assignView === 'dependency') {
            const onBypass = recoverRequests.filter((r) => Number(r.bypassableOutstanding ?? 0) > 0).length
            return [...work, { label: t('assignment.needBypass', 'Need a bypass'), value: String(onBypass),
                note: t('assignment.needBypassNote', 'startable only if worked around'),
                tint: 'bg-violet-50 dark:bg-violet-950/40' }]
        }
        return [...work, { label: t('recoverRequests.numberRequests'),
            value: statsLoading ? '\u2026' : String(stats?.uniqueRequestCount ?? 0),
            note: t('assignment.inThisQueue', 'in this queue'),
            tint: 'bg-violet-50 dark:bg-violet-950/40' }]
    }, [assignView, recoverRequests, stats, statsLoading, goCount, waitCount, workforceQuery.data, getUserStatus, t])

    const handleShowMetadata = useCallback((title: string, content: string | null) => {
        setJsonDialog({ title, content })
    }, [])

    const handleShowRunbook = useCallback((objectID: string, scenario: number | null, forecastStartTime: string | Date | null) => {
        setRunbookDialog({ objectID, scenario, forecastStartTime })
    }, [])

    const handleShowFloorPlan = useCallback((objectID: string) => {
        // TODO: Implement floor plan view
        console.log('Show floor plan for:', objectID)
    }, [])

    // ONE assign dialog, reached from everywhere. The table's row icon, the Queue's Assign
    // button and the canvas's right-click menu all funnel through here, and they were all
    // opening the older picker — a flat user dropdown with no eligibility, no site match and no
    // load — while the two newer views used the ranked one. Same object, two different answers
    // to "who should take this", depending which control you happened to click.
    //
    // The ranked dialog takes the ROW, so callers that only carry loose fields resolve it here.
    const handleAssignEngineer = useCallback((objectID: string, recoverRequestId: number | null) => {
        const row = recoverRequests.find((r) =>
            (recoverRequestId != null && r.id === recoverRequestId) || String(r.objectID) === String(objectID))
        if (row) { setRankedRow(row); return }
        // No row means no recover-request to write against, so there is nothing to open.
    }, [recoverRequests])

    const assignEngineer = useAssignEngineer()


    const handleShowObjectDetails = useCallback((objectID: string, requestID: string) => {
        setDetailsDialog({ objectID, requestID })
    }, [])

    const handleShowQRCode = useCallback((objectID: string) => {
        setQrCodeObjectID(objectID)
        setShowQRCodeDialog(true)
    }, [])

    const handleShowSubObjectProgress = useCallback((mainId: string, objectDescription?: string | null) => {
        setSubObjectProgressDialog({ mainId, objectDescription })
    }, [])

    const handleShowScenarioInfo = useCallback((requestID: string | null, scenario: number | null, objectID?: string | null, objectDescription?: string | null) => {
        setScenarioInfoModal({ requestID, scenario, objectID: objectID ?? null, objectDescription: objectDescription ?? null })
    }, [])

    const handleMarkComplete = useCallback((objectID: string, objectDescription: string | null, recoverRequestId: number | null) => {
        setMarkCompleteDialog({ objectID, objectDescription, recoverRequestId })
    }, [])

    const handleConfirmMarkComplete = useCallback(() => {
        if (!markCompleteDialog || markCompleteDialog.recoverRequestId == null) return
        markComplete.mutate(
            { requestID: markCompleteDialog.recoverRequestId },
            { onSuccess: () => setMarkCompleteDialog(null) }
        )
    }, [markCompleteDialog, markComplete])

    const columns = useMemo(
        () => createRecoverRequestColumns(t, i18n.language, handleShowMetadata, handleShowRunbook, handleShowFloorPlan, handleAssignEngineer, handleShowObjectDetails, handleShowQRCode, handleShowSubObjectProgress, handleShowScenarioInfo, handleMarkComplete, allowBypass),
        [t, i18n.language, handleShowMetadata, handleShowRunbook, handleShowFloorPlan, handleAssignEngineer, handleShowObjectDetails, handleShowQRCode, handleShowSubObjectProgress, handleShowScenarioInfo, handleMarkComplete, allowBypass]
    )

    /**
     * How many rows the policy actually moves — the number that makes the toggle mean something.
     * Without it, switching it off looks like the screen went quiet for no reason.
     */
    const bypassDependentCount = useMemo(
        () => recoverRequests.filter((r) => String(r.waitGo ?? '').toUpperCase() === 'GO'
            && Number(r.bypassableOutstanding ?? 0) > 0).length,
        [recoverRequests]
    )


    /** Format: <ObjectID>, <RequestDate>, Scenario <ScenarioName>; RequestDate uses current language */
    const formatRequestSetOption = useCallback((option: { objectID: string | null; requestDate: string | Date | null; scenarioName: string | null }) => {
        const { objectID, requestDate, scenarioName } = option
        const parts: string[] = []
        if (objectID) parts.push(objectID)
        if (requestDate) {
            const date = requestDate instanceof Date ? requestDate : new Date(requestDate)
            if (!isNaN(date.getTime())) {
                const formattedDate = new Intl.DateTimeFormat(i18n.language || 'en-US', {
                    year: 'numeric',
                    month: 'short',
                    day: '2-digit',
                    hour: '2-digit',
                    minute: '2-digit',
                    hour12: false,
                }).format(date)
                parts.push(formattedDate)
            }
        }
        if (scenarioName) parts.push(`Scenario ${scenarioName}`)
        return parts.join(', ') || ''
    }, [i18n.language])

    const handleClearFilters = useCallback(() => {
        setRequestIDFilter('')
        setLocationFilter('')
        setTeamFilter('')
        setRecoverySetFilter('')
        setScenarioFilter('')
        setSearch('')
        setPage(1)
    }, [])

    return (
        <>
            <div className="space-y-[var(--spacing-section)]" style={{ padding: 'var(--spacing-page)' }}>
                {/* Header */}
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-xl lg:text-2xl font-bold">{t('recoverRequests.title')}</h1>
                        <p className="text-muted-foreground">
                            {t('recoverRequests.description')}
                        </p>
                    </div>
                </div>

                {/* THE HEADLINE, SIZED FOR A HEADLINE AND ANSWERING THE VIEW YOU ARE IN.
                    Three 96px-tall cards with a mb-16 gutter took a third of the fold to say
                    715 / 19 / 9, and said the same three numbers whichever of the four views was
                    open. These are the compact pastel cards the Resource view already uses, and
                    the last two change with the view. */}
                <div className="mb-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                    {cards.map((c) => (
                        <QueueStat key={c.label} label={c.label} value={c.value} note={c.note} tint={c.tint} />
                    ))}
                </div>

                {/* THE FILTERS BELONG TO THE QUEUE, NOT TO THE TABLE. They were the DataTable's
                    toolbar, so they lived inside the element hidden on every view but Queue —
                    narrowing to one site or one team was impossible on precisely the views that
                    are about sites and teams. One row, above the switcher, all four views. */}
                <div className="mb-3 overflow-x-auto pb-1">
                        <div className="flex items-end gap-2 flex-nowrap">
                            {/* Search sits with the filters, not in the DataTable toolbar. In the
                                toolbar it was inside the element hidden on every view but Queue,
                                so the three views without a table had no way to search at all. */}
                            <div className="w-[240px] flex-shrink-0 flex flex-col gap-1">
                                <label className="text-xs font-medium text-muted-foreground">
                                    {t('common.search', 'Search')}
                                </label>
                                <div className="relative">
                                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                    <Input
                                        placeholder={t('recoverRequests.searchObjectID')}
                                        value={search}
                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => { setSearch(e.target.value); setPage(1) }}
                                        className="pl-9 h-10"
                                    />
                                </div>
                            </div>

                            {/* Scenario Filter */}
                            <div className="w-[200px] flex-shrink-0 flex flex-col gap-1">
                                <label className="text-xs font-medium text-muted-foreground">Scenario</label>
                                <Select value={scenarioFilter || 'all'} onValueChange={(value) => {
                                    setScenarioFilter(value === 'all' ? '' : value)
                                    setPage(1)
                                }}>
                                    <SelectTrigger>
                                        <SelectValue placeholder={t('recoverRequests.filterScenario', 'Scenario')} />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">{t('common.all')}</SelectItem>
                                        {scenarioOptions?.map((opt) => (
                                            <SelectItem key={opt.id} value={String(opt.id)}>
                                                {opt.name || '-'}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>

                            {/* Request ID Filter */}
                            <div className="w-[220px] flex-shrink-0 flex flex-col gap-1">
                                <label className="text-xs font-medium text-muted-foreground">Request</label>
                                <Select value={requestIDFilter || 'all'} onValueChange={(value) => {
                                    setRequestIDFilter(value === 'all' ? '' : value)
                                    setPage(1)
                                }}>
                                    <SelectTrigger>
                                        <SelectValue placeholder={t('recoverRequests.filterRequestID')} />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">{t('common.all')}</SelectItem>
                                        {requestIDOptions?.map((option) => (
                                            <SelectItem key={option.requestID || ''} value={option.requestID || ''}>
                                                {formatRequestSetOption(option)}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>

                            {/* Location Filter */}
                            <div className="w-[180px] flex-shrink-0 flex flex-col gap-1">
                                <label className="text-xs font-medium text-muted-foreground">Location</label>
                                <Select value={locationFilter || 'all'} onValueChange={(value) => {
                                    setLocationFilter(value === 'all' ? '' : value)
                                    setPage(1)
                                }}>
                                    <SelectTrigger>
                                        <SelectValue placeholder={t('recoverRequests.filterLocation')} />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">{t('common.all')}</SelectItem>
                                        {locationOptions?.map((location) => (
                                            <SelectItem key={location} value={location}>
                                                {location}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>

                            {/* Team Filter */}
                            <div className="w-[180px] flex-shrink-0 flex flex-col gap-1">
                                <label className="text-xs font-medium text-muted-foreground">Team</label>
                                <Select value={teamFilter || 'all'} onValueChange={(value) => {
                                    setTeamFilter(value === 'all' ? '' : value)
                                    setPage(1)
                                }}>
                                    <SelectTrigger>
                                        <SelectValue placeholder={t('recoverRequests.filterTeam')} />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">{t('common.all')}</SelectItem>
                                        {teamOptions?.map((team) => (
                                            <SelectItem key={team} value={team}>
                                                {team}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>

                            {/* Recovery Set Filter */}
                            <div className="w-[180px] flex-shrink-0 flex flex-col gap-1">
                                <label className="text-xs font-medium text-muted-foreground">Recovery Set</label>
                                <Select value={recoverySetFilter || 'all'} onValueChange={(value) => {
                                    setRecoverySetFilter(value === 'all' ? '' : value)
                                    setPage(1)
                                }}>
                                    <SelectTrigger>
                                        <SelectValue placeholder={t('recoverRequests.filterRecoverySet')} />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">{t('common.all')}</SelectItem>
                                        {recoverySetOptions?.map((set) => (
                                            <SelectItem key={set} value={set}>
                                                {set}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>

                            {/* Clear Filters Button */}
                            {(requestIDFilter || locationFilter || teamFilter || recoverySetFilter || scenarioFilter || search) && (
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={handleClearFilters}
                                    className="gap-2 flex-shrink-0"
                                >
                                    <X className="h-4 w-4" />
                                    {t('recoverRequests.clearFilters')}
                                </Button>
                            )}
                        </div>
                </div>

                {/* The view switcher and the bypass policy are both statements about the whole
                    screen, so they share a line rather than stacking two full-width bands above
                    the work. Canvas sits among the views because that is where someone looks for
                    a way to see the request, even though choosing it opens a dialog rather than
                    changing the view. */}
                <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
                    {/* Default size, not sm. Every other MAIN view switcher in the product —
                        Readiness, the recovery-set tabs — is OverflowTabsList at px-3 py-1.5
                        text-sm; at sm this read as a sub-filter of the table rather than the
                        control that changes the whole screen. */}
                    <SegmentedControl
                        ariaLabel={t('assignment.viewLabel', 'Assignment view')}
                        value={assignView}
                        onValueChange={(v) => {
                            // 'canvas' is an action rather than a view: it opens the dependency
                            // canvas and leaves whichever view is showing exactly as it was.
                            if (v === 'canvas') { if (requestIDFilter) setCanvasOpen(true); return }
                            setAssignView(v as typeof assignView)
                        }}
                        options={[
                            { value: 'existing', label: t('assignment.viewExisting', 'Queue') },
                            { value: 'dependency', label: t('assignment.viewDependency', 'Dependency order') },
                            { value: 'site', label: t('assignment.viewSite', 'By site') },
                            { value: 'workforce', label: t('assignment.viewWorkforce', 'Resource') },
                            {
                                value: 'canvas',
                                label: t('assignment.viewCanvas', 'Canvas'),
                                // One request, one dependency graph. Across several there is
                                // nothing coherent to draw, so it stays unavailable until one
                                // is chosen — and says so rather than just refusing.
                                disabled: !requestIDFilter,
                                title: requestIDFilter
                                    ? t('recoverRequests.openCanvasFor', 'Show the dependency canvas for request {{id}}', { id: requestIDFilter })
                                    : t('recoverRequests.openCanvasDisabled', 'Select a request ID to show its dependency canvas'),
                            },
                        ]}
                    />

                    <div className="flex items-center gap-2.5">
                        {bypassDependentCount > 0 && (
                            <span className="whitespace-nowrap rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-900 dark:bg-amber-950/60 dark:text-amber-200">
                                {t('recoverRequests.bypassDependentCount',
                                    '{{n}} on this page depend on it', { n: bypassDependentCount })}
                            </span>
                        )}
                        <label className="flex cursor-pointer items-center gap-2 text-sm"
                               title={allowBypass
                                   ? t('recoverRequests.allowBypassOn',
                                       'An engineer may work around a bypassable dependency — break-glass accounts, a flat recovery VLAN. Objects needing one are assignable, and the amber count is how many bypasses to apply.')
                                   : t('recoverRequests.allowBypassOff',
                                       'An object whose remaining dependencies are only bypassable is treated as blocked. Turn this on to allow those dependencies to be worked around.')}>
                            <Switch checked={allowBypass} onCheckedChange={toggleAllowBypass}
                                    aria-label={t('recoverRequests.allowBypass', 'Allow bypassable dependencies')} />
                            <span className="whitespace-nowrap font-medium">
                                {t('recoverRequests.allowBypass', 'Allow bypassable dependencies')}
                            </span>
                        </label>
                    </div>
                </div>

                {/* Only for ONE request. Across several the shape is a sum of unrelated plans,
                    so the peak would say more about how many requests are open than about any
                    recovery. */}
                {requestIDFilter && (
                    <PlannedTimelineStrip
                        rows={requestAllRows.length > 0 ? requestAllRows : recoverRequests}
                        // The queue's own rule and policy, so the strip cannot disagree with the table.
                        canAssign={(r) => effectiveIsGo(r as RecoverRequest, allowBypass)}
                    />
                )}

                {assignView === 'dependency' && (
                    <DependencyOrderView rows={recoverRequests} locale={i18n.language} onAssign={setRankedRow} allowBypass={allowBypass}                         actions={{
                            onShowRunbook: handleShowRunbook,
                            onShowObjectDetails: handleShowObjectDetails,
                            onShowFloorPlan: handleShowFloorPlan,
                            onShowQRCode: handleShowQRCode,
                            onShowSubObjectProgress: handleShowSubObjectProgress,
                            onMarkComplete: handleMarkComplete,
                        }}
                    />
                )}
                {assignView === 'site' && (
                    <BySiteView rows={recoverRequests} locale={i18n.language} onAssign={setRankedRow} allowBypass={allowBypass}                         actions={{
                            onShowRunbook: handleShowRunbook,
                            onShowObjectDetails: handleShowObjectDetails,
                            onShowFloorPlan: handleShowFloorPlan,
                            onShowQRCode: handleShowQRCode,
                            onShowSubObjectProgress: handleShowSubObjectProgress,
                            onMarkComplete: handleMarkComplete,
                        }}
                    />
                )}
                {assignView === 'workforce' && <WorkforceView rows={recoverRequests} requestID={requestIDFilter || null} />}


                {/* Table */}
                {/* Table - Full Width - Extended */}
                <div
                    hidden={assignView !== 'existing'}
                    className="overflow-x-auto [&_td]:!py-1"
                    style={{
                        marginLeft: 'calc(var(--spacing-page) * -2)',
                        marginRight: 'calc(var(--spacing-page) * -2)',
                        paddingLeft: 'calc(var(--spacing-page) * 0.05)',
                        paddingRight: 'calc(var(--spacing-page) * 0.05)',
                        width: 'calc(100vw - var(--sidebar-width-expanded) - var(--spacing-page) * 0.1)',
                        maxWidth: 'none',
                        minWidth: '1400px',
                    }}
                >
                    <DataTable
                        columns={columns}
                        data={recoverRequests}
                        getCellClassName={(row: RecoverRequest, columnId: string) => (columnId === 'objectID' ? (effectiveIsGo(row, allowBypass) ? 'border-l-4 border-l-[rgb(151,200,100)]' : 'border-l-4 border-l-transparent') : '')}
                        firstHeaderCellClassName="border-l-4 border-l-[hsl(var(--table-header))]"
                        enableRowSelection={false}
                        // No columns here are worth hiding, and the control was taking a whole
                        // row above the table to say so.
                        showColumnToggle={false}
                        pagination={{
                            page,
                            limit,
                            total: recoverRequestsResponse?.data?.total || 0,
                            onPageChange: setPage,
                            onPageSizeChange: setLimit,
                        }}
                    />
                </div>

                {/* JSON View Dialog */}
                {jsonDialog && (
                    <JsonViewDialog
                        open={!!jsonDialog}
                        onOpenChange={(open) => {
                            if (!open) setJsonDialog(null)
                        }}
                        title={jsonDialog.title}
                        content={jsonDialog.content}
                    />
                )}

                {/* Runbook View Dialog */}
                {runbookDialog && (
                    <RunbookViewDialog
                        open={!!runbookDialog}
                        onOpenChange={(open) => {
                            if (!open) setRunbookDialog(null)
                        }}
                        objectID={runbookDialog.objectID}
                        scenario={runbookDialog.scenario}
                        forecastStartTime={runbookDialog.forecastStartTime}
                    />
                )}

                {/* Recover Request Details Dialog */}
                {canvasOpen && requestIDFilter && (
                <AssignmentCanvasDialog
                    open={canvasOpen}
                    onOpenChange={setCanvasOpen}
                    requestID={requestIDFilter}
                    // The same text the Request filter shows, so the dialog title matches what
                    // was clicked instead of reciting a UUID nobody chose by.
                    requestLabel={(() => {
                        const o = requestIDOptions?.find((x) => x.requestID === requestIDFilter)
                        if (!o) return null
                        const when = o.requestDate ? formatDateForLocale(String(o.requestDate), i18n.language, { day: '2-digit', month: 'short', year: 'numeric' }) : ''
                        return [o.objectID, when].filter(Boolean).join(', ')
                    })()}
                    rows={recoverRequests}
                    allowBypass={allowBypass}
                    actions={{
                        onViewRunbook: handleShowRunbook,
                        onViewLocation: handleShowFloorPlan,
                        // The canvas already hands back the row, which is exactly what the
                        // ranked dialog wants — no unpacking needed.
                        onAssignEngineer: (r) => setRankedRow(r),
                    }}
                />
            )}

            {detailsDialog && (
                    <RecoverRequestDetailsDialog
                        open={!!detailsDialog}
                        onOpenChange={(open) => {
                            if (!open) setDetailsDialog(null)
                        }}
                        objectID={detailsDialog.objectID}
                        requestID={detailsDialog.requestID}
                    />
                )}

                {/* The ranked dialog, opened from the three new views. The original below stays
                    mounted for the existing table, so that view is genuinely unchanged. */}
                <RankedAssignDialog
                    open={rankedRow != null}
                    onOpenChange={(open) => { if (!open) setRankedRow(null) }}
                    row={rankedRow}
                    locale={i18n.language}
                    isLoading={assignEngineer.isPending}
                    onConfirm={(userID) => {
                        const row = rankedRow
                        if (!row?.id) return
                        assignEngineer.mutate(
                            { requestID: row.id, userID },
                            { onSuccess: () => setRankedRow(null) },
                        )
                    }}
                />


                {/* Scenario info modal (RecoveryScenarios + RequestSets) */}
                <ScenarioInfoDialog
                    open={!!scenarioInfoModal}
                    onOpenChange={(open) => {
                        if (!open) setScenarioInfoModal(null)
                    }}
                    requestID={scenarioInfoModal?.requestID ?? null}
                    scenario={scenarioInfoModal?.scenario ?? null}
                    objectID={scenarioInfoModal?.objectID ?? null}
                    objectDescription={scenarioInfoModal?.objectDescription ?? null}
                />

                {/* Sub-object progress dialog */}
                {subObjectProgressDialog && (
                    <SubObjectProgressDialog
                        open={!!subObjectProgressDialog}
                        onOpenChange={(open) => {
                            if (!open) setSubObjectProgressDialog(null)
                        }}
                        mainId={subObjectProgressDialog.mainId}
                        objectDescription={subObjectProgressDialog.objectDescription}
                    />
                )}

                {/* QR Code Dialog */}
                <ObjectQRCodeDialog
                    open={showQRCodeDialog}
                    onOpenChange={(open) => {
                        setShowQRCodeDialog(open)
                        if (!open) setQrCodeObjectID(null)
                    }}
                    objectID={qrCodeObjectID}
                />
            </div>

            {/* Mark Complete Confirm Dialog */}
            {markCompleteDialog && (
                <MarkCompleteConfirmDialog
                    open={!!markCompleteDialog}
                    onOpenChange={(open) => { if (!open) setMarkCompleteDialog(null) }}
                    objectID={markCompleteDialog.objectID}
                    objectDescription={markCompleteDialog.objectDescription}
                    onConfirm={handleConfirmMarkComplete}
                    isLoading={markComplete.isPending}
                />
            )}
        </>
    )
}
